import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Card, Row, Col, Space, Button, Popconfirm, Tag } from 'antd';
import { CheckCircleOutlined, LogoutOutlined } from '@ant-design/icons';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { usePwd217Query } from './hooks/usePwd217Query';
import { usePwd217Approval } from './hooks/usePwd217Approval';
import Pwd217QueryBar from './components/Pwd217QueryBar';
import Pwd217PendingList from './components/Pwd217PendingList';
import Pwd217DetailPanel from './components/Pwd217DetailPanel';
import { pwd217Api } from '../../services/pwd217Api';
import {
  BatchAcceptanceBridge,
  pwd217f1AcceptanceManifest,
  useAcceptanceMode,
} from '../../features/acceptance';

const { Title, Text } = Typography;
const acceptanceActionIds = {
  list: 'PWD217F1-ACT-QUERY',
  approve: 'PWD217F1-ACT-APPROVE',
};
void acceptanceActionIds;

const Pwd217Form: React.FC = () => {
  const navigate = useNavigate();
  const acceptanceMode = useAcceptanceMode();
  const [detailSchemaPreview, setDetailSchemaPreview] = useState(false);

  // Query hook
  const {
    startNo, endNo, setStartNo, setEndNo,
    loading, items, fetchPending,
  } = usePwd217Query();

  // Approval hook
  const {
    checkedKeys, setCheckedKeys,
    selectedDocNo, setSelectedDocNo,
    editableItems, setEditableItems,
    updateSupervisorDesc,
    approving, handleApprove,
    selectedItem,
  } = usePwd217Approval(items);

  // Sync editableItems when query results change
  useEffect(() => {
    setEditableItems(items);
    setCheckedKeys([]);
    setSelectedDocNo(null);
  }, [items, setEditableItems, setCheckedKeys, setSelectedDocNo]);

  // Approve callback
  const onApprove = useCallback(() => {
    handleApprove(fetchPending);
  }, [handleApprove, fetchPending]);

  return (
    <PermissionGuard module={Modules.PWD217} action="query">
      <div style={{ padding: '16px 24px 80px' }}>
        {/* Page Header */}
        <div style={{ marginBottom: 16 }}>
          <nav style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>
            <a href="#/erp" onClick={(e) => { e.preventDefault(); navigate('/erp'); }}>
              首頁
            </a>
            <span style={{ margin: '0 4px' }}>/</span>
            <span>採購管理</span>
            <span style={{ margin: '0 4px' }}>/</span>
            <span>入庫單異常說明-主管</span>
          </nav>
          <Title level={4} style={{ margin: 0 }}>
            入庫單異常說明-主管
            <Tag color="green" style={{ marginLeft: 8, verticalAlign: 'middle' }}>D217</Tag>
          </Title>
        </div>

        {/* Query Bar */}
        <Pwd217QueryBar
          startNo={startNo}
          endNo={endNo}
          loading={loading}
          onStartChange={setStartNo}
          onEndChange={setEndNo}
          onSearch={fetchPending}
        />

        {/* Summary & Approve Button */}
        <Card size="small" style={{ marginBottom: 12 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Space>
                <Text type="secondary">
                  共 <strong>{editableItems.length}</strong> 筆待核准
                </Text>
                {checkedKeys.length > 0 && (
                  <Text type="secondary">
                    已勾選 <strong>{checkedKeys.length}</strong> 筆
                  </Text>
                )}
              </Space>
            </Col>
            <Col>
              <Space>
                <Popconfirm
                  title={`確認存檔並核准 ${checkedKeys.length} 筆單據？`}
                  onConfirm={onApprove}
                  okText="確認核准"
                  cancelText="取消"
                  disabled={checkedKeys.length === 0}
                >
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    data-acceptance-action="PWD217F1-ACT-APPROVE"
                    loading={approving}
                    disabled={checkedKeys.length === 0}
                  >
                    存檔/核准
                  </Button>
                </Popconfirm>
                <Button
                  icon={<LogoutOutlined />}
                  onClick={() => navigate('/erp')}
                >
                  離開
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Grid1: Pending List */}
        <Card
          size="small"
          data-acceptance-action="PWD217F1-ACT-QUERY"
          title="待核准清單"
          style={{ marginBottom: 12 }}
          bodyStyle={{ padding: 0 }}
        >
          <Pwd217PendingList
            items={editableItems}
            loading={loading}
            checkedKeys={checkedKeys}
            selectedDocNo={selectedDocNo}
            onCheckedChange={setCheckedKeys}
            onSelect={setSelectedDocNo}
            onSupervisorDescChange={updateSupervisorDesc}
            isEditing={true}
          />
        </Card>

        {/* Grid2: Detail Panel — only visible when a row is selected */}
        {(selectedItem || detailSchemaPreview) && (
          <Card
            size="small"
            title={
              <span>
                異常說明明細 — 單號: <strong>{selectedItem?.doc_no || '欄位結構預覽'}</strong>
                {' '}
                <Tag color={selectedItem?.doc_type === 'D202' ? 'blue' : 'orange'}>
                  {selectedItem?.doc_type || '驗收'}
                </Tag>
              </span>
            }
          >
            <Pwd217DetailPanel
              details={selectedItem?.details || []}
            />
          </Card>
        )}
        {acceptanceMode.isEnabled(pwd217f1AcceptanceManifest.formId) && (
          <label data-acceptance-non-field style={{ display: 'inline-flex', gap: 6, marginTop: 8 }}>
            <input
              type="checkbox"
              data-contract-field="ui_detail_selected"
              checked={detailSchemaPreview}
              onChange={event => setDetailSchemaPreview(event.target.checked)}
            />
            驗收：顯示明細欄位結構
          </label>
        )}
        <BatchAcceptanceBridge
          manifest={pwd217f1AcceptanceManifest}
          records={editableItems}
          selectedKeys={checkedKeys}
          keyOf={(item) => item.doc_no}
          queryExact={async (key) => {
            const result = await pwd217Api.getPending(key, key);
            return result.data[0] || null;
          }}
          lastApiResponse={{ data: items, count: items.length }}
          loading={loading || approving}
          onAutoFill={() => {
            const first = editableItems[0];
            if (first) {
              setCheckedKeys([first.doc_no]);
              setSelectedDocNo(first.doc_no);
            }
          }}
          autoFillDisabled={editableItems.length === 0}
          autoFillReason="先查詢一筆待主管說明核准的異常單。"
          screenDraft={{ startNo, endNo, items: editableItems, selected_items: checkedKeys }}
        />
      </div>
    </PermissionGuard>
  );
};

export default Pwd217Form;
